<!--
	@name app-source-result-dialog-zero-products
	@description Dialog component for displaying when 0 matching products returned
	@date 2022/03/01
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<common-dialog ref="dialog" :show-close="!processing" persistent :max-width="1000">
		<template #header>
			<span class="font-weight-bold pl-1">
				0 matching products
			</span>
		</template>

		<template #body>
			<div class="pa-1">
				<template v-if="airIntelligence">
					<div v-if="processing" class="text-center">
						<v-progress-circular color="primary" indeterminate />
					</div>
					<v-fade-transition hide-on-leave>
						<div v-if="!processing">
							<p class="text-body-1 font-weight-bold mb-0">
								Insights powered by Air Intelligence
							</p>
							<p class="text-body-2 mb-0">
								Your request for £250,000 advance on a property valued at £500,000 would require an LTV of 50%. Recent data shows that the average market LTV is 28%, with the highest LTV of the last 72 hours being 42%.
								<a class="font-weight-bold" @click="updateCriteria(400)">Click here to request a lower initial advance.</a>
							</p>
						</div>
					</v-fade-transition>
					<v-divider class="mt-5 mb-6" />
				</template>

				<v-row>
					<v-col cols="12" :md="failures.length ? 6 : 12" class="text-center py-0">
						<v-card
							class="ga--trigger ga--outside-criteria-opened h-100"
							data-ga-id="zero-products-dialog"
							hover
							color="blue-grey lighten-5"
							@click="$emit('open-outside-criteria-products-dialog')"
						>
							<v-system-bar height="16" color="grey lighten-1" />
							<div class="pa-6">
								<p class="text-body-1 error--text font-weight-bold mb-2">
									PRODUCTS ARE OUTSIDE CRITERIA
								</p>
								<p class="text-body-2 mb-0">
									<span class="font-weight-bold">CLICK HERE</span> (or <span class="error--text font-weight-medium text-decoration-underline">{{ outsideCriteriaProductCount }} Outside Criteria</span> in the toolbar) to view all products outside criteria.
								</p>
							</div>
						</v-card>
					</v-col>

					<v-col v-if="failures.length" cols="12" md="6" class="text-center py-0">
						<v-card
							class="ga--trigger ga--retrieving-live-rates-opened h-100"
							data-ga-id="zero-products-dialog"
							hover
							color="blue-grey lighten-5"
							@click="$emit('open-product-loader-dialog')"
						>
							<v-system-bar height="16" color="grey lighten-1" />
							<div class="pa-6">
								<p class="text-body-1 error--text font-weight-bold mb-2">
									{{ failures.length }} PROVIDER{{ failures.length !== 1 ? 'S' : '' }} RETURNED 0 PRODUCTS
								</p>
								<p class="text-body-2 mb-0">
									<span class="font-weight-bold">CLICK HERE</span> (or <v-sheet :color="apiHealthStatus" class="d-inline pa-0 white--text text-decoration-underline">
										Retrieving Live Rates: Complete
									</v-sheet> in the status bar top left) to view details regarding provider APIs.
								</p>
							</div>
						</v-card>
					</v-col>
				</v-row>

				<div v-if="!airIntelligence" class="mt-6">
					<p class="text-body-1 font-weight-bold mb-1">
						Other suggestions:
					</p>
					<ul class="pl-6">
						<li class="text-body-2 mb-1">
							The <strong>search box at the top left of the product results table</strong> can help in quickly isolating one or more products.
						</li>
						<li class="text-body-2 mb-0">
							<a class="font-weight-bold" @click="openChat">Click here to open up a chat session</a> with one of our support team (9am - 5pm Monday to Friday)
						</li>
					</ul>
				</div>
			</div>
		</template>
	</common-dialog>
</template>

<script>
	import { EventBus } from '@/utils';
	import CommonDialog from '@/component/common/dialog';

	export default {
		name: 'app-source-result-dialog-zero-products',

		components: { CommonDialog },

        props: {
			apiHealthStatus: { type: String, required: true },
			outsideCriteriaProductCount: { type: Number, required: true, default: 0 },
			providers: { type: Array, required: true }
		},

		data() {
			return {
                airIntelligence: false,
                processing: true
            };
		},

        computed: {
			failures() {
				return this.providers.filter((p) => p.status && p.status.toLowerCase() === 'failed');
			}
		},

		methods: {
			/**
			 * @name open
			 * @description Open the dialog
			 */
			open(title) {
                this.processing = true;
				this.title = title;
				this.$refs.dialog.open();
                // Google Analytics event
                window.dataLayer.push({
                    event: 'zeroProductsDialogOpened',
                    'gtm.element.dataset.gaId': 'results',
                    'gtm.element.dataset.gaValue': 'zero matching products'
                });
                setTimeout(() => {
                    this.processing = false;
                }, 1000);
			},

			/**
			 * @name close
			 * @description Close the dialog
			 */
			close() {
				this.$refs.dialog.close();
			},

			/**
			 * @name refresh
			 * @description Refresh the page
			 */
			refresh() {
				window.location.href = window.location.href + '&view=reload';
			},

            /**
			 * @name updateCriteria
			 * @description Open relevant section on filter
			 */
			updateCriteria(restrictionCode) {
				this.$refs.dialog.close();
				this.$emit('update-criteria', restrictionCode);
			},

            /**
			 * @name openChat
			 */
			openChat() {
				EventBus.$emit('open-chat');
			}
		}
	};
</script>
